'use client'

import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import { postApiProductsGetProductsList, type ProductListItem } from '../../../api/generated'
import { colors } from '../../../constants/colors'
import FilterStorage from '../../../storage/FilterStorage/FilterStorage'
import { breakpoints } from '../../breakPoints'

type Props = {
  compact?: boolean
  onClickFilter?: () => void
}

export const ProductSearch = ({ compact = false, onClickFilter }: Props) => {
  const router = useRouter()
  const pathname = usePathname()
  const filterStore = useContext(FilterStorage)
  const [openProducts, setOpenProducts] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [productsList, setProductsList] = useState<ProductListItem[]>([])
  const [isProductsLoading, setIsProductsLoading] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    setSearchValue(params.get('searchValue') || params.get('value') || '')
  }, [])

  useEffect(() => {
    let mounted = true

    const loadProducts = async () => {
      if (searchValue.length <= 1) {
        setProductsList([])

        return
      }

      setIsProductsLoading(true)

      try {
        const response = await postApiProductsGetProductsList({
          productNameTerm: searchValue,
          skip: 0,
          take: 20,
        })

        if (mounted) {
          setProductsList(response?.items || [])
        }
      } catch {
        if (mounted) {
          setProductsList([])
        }
      } finally {
        if (mounted) {
          setIsProductsLoading(false)
        }
      }
    }

    const timer = window.setTimeout(loadProducts, 350)

    return () => {
      mounted = false
      window.clearTimeout(timer)
    }
  }, [searchValue])

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    router.push(`/search?searchValue=${encodeURIComponent(searchValue)}`)
  }

  const handleSearchClear = () => {
    setSearchValue('')
    filterStore.clear()

    if (pathname === '/search') {
      router.replace('/search')
    }
  }

  return (
    <SearchWrapper $compact={compact}>
      <SearchComponent>
        <SearchForm onSubmit={handleSearchSubmit}>
          <MobileLogoLink href={'/'} aria-label={'На главную'}>
            <MobileLogo alt={''} src={'/images/allsharinglogo.svg'} />
          </MobileLogoLink>
          <SearchFieldSurface>
            <StyledInput
              aria-label={'Поиск по названию'}
              onChange={event => setSearchValue(event.target.value)}
              onFocus={() => setOpenProducts(true)}
              placeholder={'Поиск'}
              type={'search'}
              value={searchValue}
            />
            {(compact || searchValue) && (
              <ClearSearchButton
                aria-label={'Очистить поиск'}
                onClick={handleSearchClear}
                type={'button'}
              >
                <img
                  alt={''}
                  src={compact ? '/images/category-header/clear.svg' : '/images/buttons/close.png'}
                />
              </ClearSearchButton>
            )}
          </SearchFieldSurface>
          {onClickFilter && (
            <MobileFilterButton aria-label={'Фильтры'} onClick={onClickFilter} type={'button'}>
              {/* <img alt={''} src={'/images/buttons/filter-primary.svg'} /> */}
            </MobileFilterButton>
          )}
          <DesktopSearchButton type={'submit'}>Найти</DesktopSearchButton>
        </SearchForm>

        {searchValue.length > 1 && (
          <DropdownContent $openMenu={openProducts}>
            {isProductsLoading ? (
              <DropdownMessage>Загрузка...</DropdownMessage>
            ) : (
              productsList.map((product, id) => (
                <DropdownButton
                  $isActive={false}
                  href={`/item/${product.productId}`}
                  key={product.productId || id}
                  onClick={() => setOpenProducts(false)}
                >
                  <p>{product.name}</p>
                </DropdownButton>
              ))
            )}
            {!isProductsLoading && productsList.length === 0 && (
              <DropdownMessage>Нет результатов</DropdownMessage>
            )}
          </DropdownContent>
        )}
      </SearchComponent>
    </SearchWrapper>
  )
}

const SearchWrapper = styled.div<{ $compact: boolean }>`
  min-width: 260px;
  max-width: 720px;
  flex: 1 1 720px;

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    min-width: 0;
    max-width: none;
    flex-basis: auto;
  }

  ${({ $compact }) =>
    $compact &&
    `
    width: 100%;
    min-width: 0;
    max-width: none;
    flex: none;

    form {
      height: 38px;
      gap: 8px;
      padding: 4px 10px;
      border: 0;
      background: ${colors.lightGray};
      border-radius: 15px;
    }

    form > a { display: flex; }
    form > a img { width: 25px; height: 20px; }
    form > div { background: transparent; border-radius: 0; }
    input { padding: 0; font-size: 14px; font-weight: 600; }
    input::placeholder { color: ${colors.darkGrayText}; }
    button[type='submit'] { display: none; }
    button[aria-label='Очистить поиск'] { width: 16px; }
    button[aria-label='Очистить поиск'] img { width: 16px; height: 16px; padding: 2.83px; }
  `}
`

const MobileLogoLink = styled(Link)`
  display: none;

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
  }
`

const MobileLogo = styled.img`
  width: 28px;
  height: 26px;
  object-fit: contain;
`

const SearchComponent = styled.div`
  position: relative;
  width: 100%;
`

const SearchForm = styled.form`
  width: 100%;
  height: 38px;
  display: flex;
  align-items: center;
  border: 2px solid ${colors.primary};
  background: ${colors.primary};
  border-radius: 12px;
  overflow: hidden;

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    height: 38px;
    gap: 9px;
    padding: 0 14px 0 10px;
    border: 0;
    background: ${colors.lightGray};
    border-radius: 15px;
  }
`

const SearchFieldSurface = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
  min-width: 0;
  display: flex;
  flex: 1;
  align-items: center;
  overflow: hidden;
  border-radius: 10px;
  background: ${colors.lightGray};

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    background: transparent;
    border-radius: 0;
  }
`

const StyledInput = styled.input`
  min-width: 0;
  height: 100%;
  flex: 1;
  outline: none;
  border: none;
  padding: 0 18px;
  background: transparent;
  color: ${colors.black};
  font-size: 17px;
  font-style: normal;
  font-weight: 400;
  line-height: 1.3;

  &::placeholder {
    color: #9da2a2;
    opacity: 1;
  }

  &::-webkit-search-cancel-button {
    display: none;
  }

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    padding: 0;
    font-size: 16px;
    font-weight: 500;
  }
`

const ClearSearchButton = styled.button`
  width: 42px;
  height: 100%;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;

  img {
    width: 14px;
    height: 14px;
  }

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    width: 24px;
  }
`

const DesktopSearchButton = styled.button`
  align-self: stretch;
  min-width: 106px;
  margin-left: -26px;
  border: 0;
  padding: 0 24px 0 48px;
  background: ${colors.primary};
  color: ${colors.white};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${colors.primaryActive};
  }

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    display: none;
  }
`

const MobileFilterButton = styled.button`
  display: none;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;

  @media (width < ${breakpoints.productCardsDesktopMin}) {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;

    img {
      width: 20px;
      height: 20px;
    }
  }
`

const DropdownContent = styled.div<{ $openMenu: boolean }>`
  position: absolute;
  z-index: 1500;
  top: calc(100% + 8px);
  left: 0;
  flex-direction: column;
  align-items: stretch;
  justify-content: start;
  box-sizing: border-box;
  border-radius: 15px;
  max-height: 80vh;
  padding: 15px 0;
  font-size: 14px;
  display: ${props => (props.$openMenu ? 'flex' : 'none')};
  overflow: hidden;
  overflow-y: scroll;
  background: #fff;
  width: 100%;
  box-shadow: 9px 9px 15.7px 0px rgba(94, 94, 94, 0.07);
`

const DropdownButton = styled(Link)<{ $isActive?: boolean }>`
  color: #a0a6a6;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border: 1px solid rgba(0, 0, 0, 0);
  background: ${props => (props.$isActive ? '#008C8C70' : '#fff')};
  cursor: pointer;
  border-bottom: 1px solid #e0e5e6;
  height: 68px;
  padding: 12.5px;

  p {
    font-size: 16px;
    font-weight: 500;
    width: 100%;
    margin: 0;
    line-height: normal;
  }
`

const DropdownMessage = styled.p`
  margin: 0;
  padding: 12.5px;
  text-align-last: start;
`
